import React, { useState } from 'react';
import { ChevronRightIcon, MessageSquareIcon, BookmarkIcon } from 'lucide-react';
import { NotesTable } from './NotesTable';
import { NoteDetail } from './NoteDetail';
import { NoteTypesManagement } from './NoteTypesManagement';
import { VisibilityProfilesManagement } from './VisibilityProfilesManagement';
export type NoteVisibility = 'Public' | 'Role-based' | 'User-specific' | 'Creator-only';
export type NoteType = {
  id: string;
  name: string;
  description: string;
  defaultVisibility: NoteVisibility;
  isEditable: boolean;
  isSystemProtected: boolean;
  createdAt: string;
  updatedAt: string;
};
export type Note = {
  id: string;
  moduleType: string;
  moduleId: string;
  moduleName: string;
  noteTypeId: string;
  noteTypeName: string;
  visibility: NoteVisibility;
  visibilityDetails?: string;
  body: string;
  authorId: string;
  authorName: string;
  hasAttachment: boolean;
  attachmentName?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
};
export type NoteHistoryEntry = {
  id: string;
  noteId: string;
  action: 'Created' | 'Modified' | 'Visibility Changed' | 'Attachment Added' | 'Attachment Removed';
  userId: string;
  userName: string;
  timestamp: string;
  details?: string;
};
export type VisibilityProfile = {
  id: string;
  name: string;
  description: string;
  visibility: NoteVisibility;
  visibilityDetails?: string;
  createdAt: string;
  updatedAt: string;
};
export function NotesManagement({
  locale = 'en-US'
}) {
  const [activeView, setActiveView] = useState<'notes' | 'noteTypes' | 'visibilityProfiles'>('notes');
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  // Sample note types
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([{
    id: 'notetype-001',
    name: 'General Comment',
    description: 'Standard note for general comments',
    defaultVisibility: 'Public',
    isEditable: true,
    isSystemProtected: false,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'notetype-002',
    name: 'Approval Note',
    description: 'Notes related to approval decisions',
    defaultVisibility: 'Role-based',
    isEditable: true,
    isSystemProtected: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'notetype-003',
    name: 'Audit Comment',
    description: 'Notes added during audit reviews',
    defaultVisibility: 'Role-based',
    isEditable: false,
    isSystemProtected: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'notetype-004',
    name: 'Personal Reminder',
    description: 'Private notes visible only to creator',
    defaultVisibility: 'Creator-only',
    isEditable: true,
    isSystemProtected: false,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'notetype-005',
    name: 'Vendor Communication',
    description: 'Notes about communication with vendors',
    defaultVisibility: 'User-specific',
    isEditable: true,
    isSystemProtected: false,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }]);
  // Sample visibility profiles
  const [visibilityProfiles, setVisibilityProfiles] = useState<VisibilityProfile[]>([{
    id: 'visprofile-001',
    name: 'Finance Team Only',
    description: 'Visible only to Finance team members',
    visibility: 'Role-based',
    visibilityDetails: 'Finance Manager, Finance Officer',
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'visprofile-002',
    name: 'Procurement Team Only',
    description: 'Visible only to Procurement team members',
    visibility: 'Role-based',
    visibilityDetails: 'Procurement Manager, Procurement Officer',
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'visprofile-003',
    name: 'Management Team',
    description: 'Visible to management team members',
    visibility: 'Role-based',
    visibilityDetails: 'Finance Manager, Procurement Manager, Executive',
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'visprofile-004',
    name: 'Auditors Only',
    description: 'Visible only to auditors',
    visibility: 'Role-based',
    visibilityDetails: 'Auditor',
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }]);
  // Sample notes data
  const [notes, setNotes] = useState<Note[]>([{
    id: 'note-001',
    moduleType: 'PurchaseOrder',
    moduleId: 'PO-2023-001',
    moduleName: 'Server Hardware',
    noteTypeId: 'notetype-001',
    noteTypeName: 'General Comment',
    visibility: 'Public',
    body: 'Vendor confirmed delivery for next Tuesday. Please ensure someone is available to receive the goods.',
    authorId: 'user-001',
    authorName: 'John Smith',
    hasAttachment: false,
    createdAt: '2023-10-15T09:30:00Z',
    updatedAt: '2023-10-15T09:30:00Z',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-001',
    siteName: 'New York HQ'
  }, {
    id: 'note-002',
    moduleType: 'Invoice',
    moduleId: 'INV-2023-002',
    moduleName: 'Office Supplies',
    noteTypeId: 'notetype-003',
    noteTypeName: 'Audit Comment',
    visibility: 'Role-based',
    visibilityDetails: 'Finance Manager, Auditor',
    body: 'Tax code appears incorrect. Please verify before processing payment.',
    authorId: 'user-005',
    authorName: 'Robert Brown',
    hasAttachment: true,
    attachmentName: 'tax_code_reference.pdf',
    createdAt: '2023-10-16T14:15:00Z',
    updatedAt: '2023-10-16T14:15:00Z',
    tags: ['Tax Issue', 'Needs Review'],
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-001',
    siteName: 'New York HQ'
  }, {
    id: 'note-003',
    moduleType: 'Requisition',
    moduleId: 'REQ-2023-003',
    moduleName: 'Marketing Materials',
    noteTypeId: 'notetype-002',
    noteTypeName: 'Approval Note',
    visibility: 'Public',
    body: 'Approved with budget exception. This is a one-time approval for this quarter only.',
    authorId: 'user-001',
    authorName: 'John Smith',
    hasAttachment: false,
    createdAt: '2023-10-10T11:45:00Z',
    updatedAt: '2023-10-10T13:20:00Z',
    tags: ['Budget Exception', 'Approved'],
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-002',
    siteName: 'San Francisco Office'
  }, {
    id: 'note-004',
    moduleType: 'Vendor',
    moduleId: 'vendor-002',
    moduleName: 'Dell Technologies',
    noteTypeId: 'notetype-005',
    noteTypeName: 'Vendor Communication',
    visibility: 'User-specific',
    visibilityDetails: 'John Smith, Jane Doe',
    body: 'Discussed volume discount for Q4. They agreed to 5% if we commit to $50K minimum spend.',
    authorId: 'user-002',
    authorName: 'Jane Doe',
    hasAttachment: true,
    attachmentName: 'dell_proposal.docx',
    createdAt: '2023-10-18T10:00:00Z',
    updatedAt: '2023-10-18T10:00:00Z',
    tags: ['Discount', 'Q4 Planning'],
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.'
  }, {
    id: 'note-005',
    moduleType: 'Budget',
    moduleId: 'budget-2023-q4',
    moduleName: 'Q4 2023 Marketing Budget',
    noteTypeId: 'notetype-004',
    noteTypeName: 'Personal Reminder',
    visibility: 'Creator-only',
    body: 'Need to follow up with Sarah about the events budget allocation before month-end.',
    authorId: 'user-001',
    authorName: 'John Smith',
    hasAttachment: false,
    createdAt: '2023-10-19T16:30:00Z',
    updatedAt: '2023-10-19T16:30:00Z',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.'
  }]);
  // Sample note history data for note-003
  const [noteHistory, setNoteHistory] = useState<NoteHistoryEntry[]>([{
    id: 'history-001',
    noteId: 'note-003',
    action: 'Created',
    userId: 'user-001',
    userName: 'John Smith',
    timestamp: '2023-10-10T11:45:00Z',
    details: 'Note created'
  }, {
    id: 'history-002',
    noteId: 'note-003',
    action: 'Modified',
    userId: 'user-001',
    userName: 'John Smith',
    timestamp: '2023-10-10T13:20:00Z',
    details: 'Content updated'
  }]);
  // Add handler for breadcrumb navigation
  const handleBreadcrumbClick = (level: string) => {
    if (level === 'settings') {
      // In a real app, this would navigate to settings page
      // For now, we'll just reset to main view
      setSelectedNote(null);
      setActiveView('notes');
    } else if (level === 'notes') {
      setSelectedNote(null);
    }
  };
  const handleViewNoteDetail = (note: Note) => {
    setSelectedNote(note);
  };
  const handleBackToNotes = () => {
    setSelectedNote(null);
  };
  return <div data-id="element-17585">
      <div className="mb-8" data-id="element-17586">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-17587">Notes Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17588">
          View, audit, and manage notes across the system
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-17589">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-17590">
          <button className={`${activeView === 'notes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveView('notes')} data-id="element-17591">
            Notes
          </button>
          <button className={`${activeView === 'noteTypes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveView('noteTypes')} data-id="element-17592">
            Note Types
          </button>
          <button className={`${activeView === 'visibilityProfiles' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveView('visibilityProfiles')} data-id="element-17593">
            Visibility Profiles
          </button>
        </nav>
      </div>
      {/* Content based on active view */}
      {activeView === 'notes' && !selectedNote && <NotesTable notes={notes} onViewNote={handleViewNoteDetail} locale={locale} data-id="element-17594" />}
      {activeView === 'notes' && selectedNote && <NoteDetail note={selectedNote} history={noteHistory.filter(h => h.noteId === selectedNote.id)} onBack={handleBackToNotes} locale={locale} data-id="element-17595" />}
      {activeView === 'noteTypes' && <NoteTypesManagement noteTypes={noteTypes} setNoteTypes={setNoteTypes} locale={locale} data-id="element-17596" />}
      {activeView === 'visibilityProfiles' && <VisibilityProfilesManagement visibilityProfiles={visibilityProfiles} setVisibilityProfiles={setVisibilityProfiles} locale={locale} data-id="element-17597" />}
    </div>;
}